import React, { useState, useEffect, useRef } from 'react';
import { FileCheck, Upload, Play, Square, Trash2, RefreshCw, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const TermsCheckerV3 = () => {
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  // Form states
  const [brandName, setBrandName] = useState('');
  const [brandUrl, setBrandUrl] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [uploadedCount, setUploadedCount] = useState(0);

  // Processing states
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);

  // Data states
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandDetails, setBrandDetails] = useState(null);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  const fileInputRef = useRef(null);
  const logsEndRef = useRef(null);

  // Fetch brands on mount
  useEffect(() => {
    fetchBrands();
    fetchUploadedBacklinks();
  }, []);

  // Poll status while processing
  useEffect(() => {
    let interval;
    if (isProcessing) {
      interval = setInterval(() => {
        fetchStatus();
        fetchLogs();
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isProcessing]);

  // Auto-scroll logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const fetchBrands = async () => {
    setIsLoadingBrands(true);
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/brands`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setBrands(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setIsLoadingBrands(false);
    }
  };

  const fetchUploadedBacklinks = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/uploaded-backlinks`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setUploadedCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching uploaded backlinks:', error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setStatus(data.data);
        if (!data.data.isProcessing) {
          setIsProcessing(false);
          fetchBrands();
        }
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/logs`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setLogs(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchBrandDetails = async (brandId) => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/brands/${brandId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setBrandDetails(data.data);
      }
    } catch (error) {
      console.error('Error fetching brand details:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCsvContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleUploadCSV = async () => {
    if (!csvContent.trim()) {
      alert('Please enter or upload CSV content first');
      return;
    }

    setIsUploading(true);
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/upload-csv`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csvContent })
      });

      const data = await response.json();
      if (data.success) {
        setUploadedCount(data.count);
        alert(`Successfully uploaded ${data.count} backlinks`);
        setCsvContent('');
      } else {
        alert(data.message || 'Failed to upload CSV');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearBacklinks = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/uploaded-backlinks`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setUploadedCount(0);
      }
    } catch (error) {
      console.error('Error clearing backlinks:', error);
    }
  };

  const handleStart = async () => {
    if (!brandName.trim() || !brandUrl.trim()) {
      alert('Please enter brand name and URL');
      return;
    }

    if (uploadedCount === 0) {
      alert('Please upload CSV backlinks first');
      return;
    }

    setIsProcessing(true);
    setLogs([]);

    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandName: brandName.trim(),
          brandUrl: brandUrl.trim()
        })
      });

      const data = await response.json();
      if (!data.success) {
        setIsProcessing(false);
        alert(data.message || 'Failed to start processing');
      }
    } catch (error) {
      console.error('Error starting processing:', error);
      setIsProcessing(false);
      alert('Failed to start processing');
    }
  };

  const handleStop = async () => {
    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/stop`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error stopping:', error);
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm('Are you sure you want to delete this brand?')) return;

    try {
      const response = await fetch(`${basePath}/api/terms-checker-v3/brands/${brandId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        fetchBrands();
        if (selectedBrand === brandId) {
          setSelectedBrand(null);
          setBrandDetails(null);
        }
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
    }
  };

  const getLogTypeColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getClassificationBadge = (classification) => {
    switch (classification) {
      case 'AFFILIATE':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">AFFILIATE</span>;
      case 'BRAND':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">BRAND</span>;
      case 'NOT_AFFILIATE':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">NOT AFFILIATE</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">PENDING</span>;
    }
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3">
            <FileCheck className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Terms Checker V3</h1>
              <p className="text-gray-600">Upload CSV backlinks and classify affiliates</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Upload & Start */}
        <div className="space-y-6">
          {/* CSV Upload Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Backlinks CSV
            </h2>

            {/* Upload status */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <span className="text-blue-800">
                {uploadedCount > 0 ? (
                  <><CheckCircle className="w-4 h-4 inline mr-2" />{uploadedCount} backlinks uploaded</>
                ) : (
                  <><AlertCircle className="w-4 h-4 inline mr-2" />No backlinks uploaded</>
                )}
              </span>
              {uploadedCount > 0 && (
                <button
                  onClick={handleClearBacklinks}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Clear
                </button>
              )}
            </div>

            {/* File input */}
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,.txt"
              onChange={handleFileUpload}
              className="hidden"
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full mb-4 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              Click to upload CSV file
            </button>

            {/* Manual input */}
            <textarea
              value={csvContent}
              onChange={(e) => setCsvContent(e.target.value)}
              placeholder="Or paste domains here (one per line):&#10;example.com&#10;anothersite.co.uk&#10;testdomain.net"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            />

            <button
              onClick={handleUploadCSV}
              disabled={isUploading || !csvContent.trim()}
              className={`w-full mt-4 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 ${
                isUploading || !csvContent.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload Backlinks
            </button>
          </div>

          {/* Start Processing Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
              <Play className="w-5 h-5" />
              Start Processing
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g., Bet365"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand URL</label>
                <input
                  type="text"
                  value={brandUrl}
                  onChange={(e) => setBrandUrl(e.target.value)}
                  placeholder="e.g., bet365.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Status */}
              {status && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span>Progress:</span>
                    <span className="font-medium">{status.processedCount} / {status.totalCount}</span>
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all"
                      style={{ width: `${status.progress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleStart}
                  disabled={isProcessing || uploadedCount === 0}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 ${
                    isProcessing || uploadedCount === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {isProcessing ? 'Processing...' : 'Start'}
                </button>

                {isProcessing && (
                  <button
                    onClick={handleStop}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                  >
                    <Square className="w-4 h-4" />
                    Stop
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Logs Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-800 mb-4">Processing Logs</h2>
            <div className="h-64 overflow-y-auto bg-gray-900 rounded-lg p-3 font-mono text-xs">
              {logs.length === 0 ? (
                <p className="text-gray-500">No logs yet...</p>
              ) : (
                logs.slice().reverse().map((log, i) => (
                  <div key={log.id || i} className={`mb-1 ${getLogTypeColor(log.type)}`}>
                    <span className="text-gray-500">[{log.time}]</span> {log.message}
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        </div>

        {/* Right Column - Results */}
        <div className="space-y-6">
          {/* Brands List */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-800">Processed Brands</h2>
              <button
                onClick={fetchBrands}
                disabled={isLoadingBrands}
                className="p-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingBrands ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {brands.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No brands processed yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {brands.map((brand) => (
                  <div
                    key={brand.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedBrand === brand.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setSelectedBrand(brand.id);
                      fetchBrandDetails(brand.id);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{brand.brand_name}</p>
                        <p className="text-sm text-gray-500">{brand.brand_url}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded ${
                          brand.status === 'completed' ? 'bg-green-100 text-green-800' :
                          brand.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {brand.status}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBrand(brand.id);
                          }}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-4 text-sm">
                      <span className="text-gray-600">Backlinks: {brand.total_backlinks}</span>
                      <span className="text-green-600">Affiliates: {brand.affiliates_found}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Brand Details */}
          {brandDetails && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-medium text-gray-800 mb-4">
                {brandDetails.brand.brand_name} - Classifications
              </h2>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-800">{brandDetails.stats.total}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{brandDetails.stats.affiliates}</p>
                  <p className="text-xs text-gray-500">Affiliates</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{brandDetails.stats.brands}</p>
                  <p className="text-xs text-gray-500">Brands</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-yellow-600">{brandDetails.stats.pending}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>

              {/* Backlinks list */}
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Domain</th>
                      <th className="text-left p-2">Score</th>
                      <th className="text-left p-2">Classification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandDetails.backlinks.map((bl, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="p-2">
                          <a
                            href={`https://${bl.backlink_domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {bl.backlink_domain}
                          </a>
                        </td>
                        <td className="p-2">{bl.domain_ascore}</td>
                        <td className="p-2">{getClassificationBadge(bl.classification)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsCheckerV3;
